import { useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, isRegisterPending } from "../../api/api";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import {
  Captcha,
  captchaRequired,
  captchaUnsupported,
  useAuthProviders,
  type CaptchaHandle,
} from "../../auth/Captcha";
import { track } from "../../lib/analytics";
import { ErrorBox, Spinner } from "../../components/ui";
import { postLoginPath } from "../../lib/nav";
import { AuthShell } from "./AuthShell";

export default function RegisterPage() {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Where verification is required there is no session yet: the account
  // signs in after (or before) confirming the emailed link.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const providers = useAuthProviders();
  const captcha = providers.data?.captcha;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaHandle>(null);
  const captchaPending =
    (captchaRequired(captcha) && !captchaToken) || captchaUnsupported(captcha);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.register({
        email, password, name, captcha_token: captchaToken ?? undefined,
      });
      track("register_completed");
      if (isRegisterPending(result)) {
        setPendingEmail(result.email || email);
        setPassword("");
        return;
      }
      setUser(result);
      navigate(postLoginPath(from), { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.status === 409 ? t("auth.emailTaken") : apiErr.message);
      // The token was spent on this attempt — a retry needs a fresh one.
      captchaRef.current?.reset();
    } finally {
      setBusy(false);
    }
  }

  if (pendingEmail) {
    return (
      <AuthShell title={t("auth.signUp")}>
        <div className="space-y-4">
          <p className="text-sm">
            {t("auth.registerCheckEmail", {
              defaultValue:
                "Check your inbox: we sent a confirmation link to {{email}}. Open it to activate your account, then sign in.",
              email: pendingEmail,
            })}
          </p>
          <Link to="/login" state={location.state} className="btn-primary w-full justify-center">
            {t("auth.signIn")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("auth.signUp")}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label" htmlFor="name">{t("auth.name")}</label>
          <input
            id="name"
            className="input mt-1"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="email">{t("auth.email")}</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            className="input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="password">{t("auth.password")}</label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            className="input mt-1"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="text-xs text-muted mt-1">{t("auth.passwordHint")}</div>
        </div>
        <Captcha ref={captchaRef} config={captcha} onToken={setCaptchaToken} />
        {error && <ErrorBox message={error} />}
        <button
          type="submit"
          className="btn-primary w-full justify-center"
          disabled={busy || captchaPending}
        >
          {busy ? <Spinner /> : t("auth.signUp")}
        </button>
        {providers.data?.email_verification_required && (
          <p className="text-xs text-muted">
            {t("auth.verificationHint", {
              defaultValue: "We'll send a confirmation link to this address — confirm it to activate your account.",
            })}
          </p>
        )}
      </form>
      <div className="mt-4 text-sm text-muted">
        {t("auth.haveAccount")}{" "}
        <Link className="text-brand hover:underline" to="/login" state={location.state}>{t("auth.signIn")}</Link>
      </div>
    </AuthShell>
  );
}
